<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

import { formatMetadata } from "@/common/utils/common";
import { toast } from "@/common/utils/toast";

import { formQuestionList } from "@/common/api/rest/form/question";
import { methodQuestionList } from "@/common/api/rest/method/method-question";
import { analysisFormList } from "@/common/api/rest/analysis/form";
import { analysisMethodList } from "@/common/api/rest/analysis/method";
import {
  analysisAnswerShow,
  analysisAnswerPatch,
} from "@/common/api/rest/analysis/answer";

import type { AnalysisType } from "@/common/types/analysis";
import type { FormType } from "@/common/types/form";
import type { AnswersType, QuestionType } from "@/common/types/question";

import { viewQuestions } from "@/components";

const { t } = useI18n();

const route = useRoute();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: AnalysisType;
    width?: string;
    isEdit?: boolean;
  }>(),
  {
    width: "100%",
    isEdit: true,
  },
);

const answers = ref<AnswersType | null>(null);
const formData = ref<FormType[]>([]);
const methodData = ref<any[]>([]);
const questionData = ref<{ [key: string]: QuestionType[] }>({});

const accountId = computed(() => route.params.id as string);

async function fetchForms() {
  if (!props.data) return;

  await analysisFormList(props.data.account_id, {
    analysis_id: props.data.analysis_id,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        formData.value = data.list;
        data.list.forEach(async (item: FormType) => {
          await formQuestionList(accountId.value, item.form_id, { full: true })
            .then((response) => {
              const { success, data, message } = response;

              if (success) {
                questionData.value[item.form_id] = data.list;
              } else {
                toast({ message: message, type: "error" });
              }
            })
            .catch(() => {
              toast({ message: t("error.default"), type: "error" });
            });
        });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function fetchMethods() {
  if (!props.data) return;

  await analysisMethodList(props.data.account_id, {
    evaluation_id: props.data.evaluation_id,
    analysis_id: props.data.analysis_id,
    full: true,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        methodData.value = data.list;
        data.list.forEach(async (item: any) => {
          await methodQuestionList(item.account_id, item.group_id, {
            full: true,
          })
            .then((response) => {
              console.log("response", response);
              const { success, data, message } = response;

              if (success) {
                questionData.value[item.form_id] = data.list;
              } else {
                toast({ message: message, type: "error" });
              }
            })
            .catch(() => {
              toast({ message: t("error.default"), type: "error" });
            });

          if (item.metadata) {
            item.metadata?.forEach((field: any) => {
              if (field.type === "RANGE") {
                if (!questionData.value[item.category_id]) {
                  questionData.value[item.category_id] = [];
                }

                questionData.value[item.category_id].push({
                  ...field,
                  question_id: field.field_id,
                  type: "INPUT",
                });
              }
            });
          }
        });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function fetchAnswers() {
  if (!props.data) return;

  await analysisAnswerShow(props.data.account_id, props.data.answer_id)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        answers.value = data;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function patchAnswers(payload: any) {
  console.log("payload", payload);
  await analysisAnswerPatch(props.data.account_id, props.data.answer_id, {
    metadata: JSON.stringify(payload),
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        fetchAnswers();
        fetchForms();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

onMounted(async () => {
  await fetchAnswers();
  await fetchForms();
  await fetchMethods();
});
</script>

<template>
  <section class="analyse-tab" :class="{ width: width }">
    <div class="content">
      <template v-if="formData.length === 0 && methodData.length === 0">
        <div class="form-empty">
          {{ t("analysis.texts.empty_questions") }}
        </div>
      </template>

      <template v-if="formData.length > 0 && data.answer_id">
        <div class="title">
          {{ t("analysis.labels.form_questions") }}
        </div>

        <template v-for="(item, index) in formData" :key="index">
          <view-questions
            field="form"
            :account-id="accountId"
            :code="item.code"
            :name="item.name"
            :answers-id="data.answer_id"
            :answers="answers"
            :owner-id="item.form_id"
            :orders="formatMetadata(item.metadata)?.['orders'] ?? []"
            :questions="questionData[item.form_id] ?? []"
            @update:data="patchAnswers($event)"
          />
        </template>
      </template>

      <template v-if="methodData.length > 0 && data.answer_id">
        <div class="title">
          {{ t("analysis.labels.method_questions") }}
        </div>

        <template v-for="(item, index) in methodData" :key="index">
          <view-questions
            field="method"
            :code="item.code"
            :name="item.name"
            :account-id="data.account_id"
            :answers-id="data.answer_id"
            :answers="answers"
            :owner-id="item.method_id"
            :orders="[]"
            :questions="questionData[item.category_id] ?? []"
            @update:data="patchAnswers($event)"
          />
        </template>
      </template>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.analyse-tab {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-block-end: 2rem;

  .title {
    display: inline-flex;
    align-items: center;
    width: calc(100% - 3rem);
    gap: 1rem;
    font-size: 1.15rem;
    font-weight: bold;
    text-transform: uppercase;
    background: #f1f1f1;
    padding-block: 0.75rem;
    padding-inline: 1.5rem;
  }

  .form-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
    padding-block: 1.5rem;
  }
}
</style>
